import CustomButton from "@components/buttons/CustomButton";
import CheckBox from "@components/inputs/CheckBox";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { invoiceServices } from "@services/apis/Invoice";
import { GetOfframpBanksData } from "@services/models/InvoiceApprovals/GetOfframpBanksModel";
import { UpdateOfframpBankRequest } from "@services/models/InvoiceApprovals/UpdateOfframpBankModel";
import { useEffect, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface EditBankSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onBankUpdated?: () => void;
  bankData?: GetOfframpBanksData | null;
}

const EditBankSlider: React.FC<EditBankSliderProps> = ({
  isOpen,
  onClose,
  onBankUpdated,
  bankData,
}) => {
  const [bankName, setBankName] = useState<string>("");
  const [accountNo, setAccountNo] = useState<string>("");
  const [accountName, setAccountName] = useState<string>("");
  const [accountType, setAccountType] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { handleError, handleSuccess } = useSnackbarMessage();

  const handleSubmit = async () => {
    if (!bankData?.bankId) {
      handleError({ message: "Bank ID is required" });
      return;
    }
    if (!bankName.trim()) {
      handleError({ message: "Please enter bank name" });
      return;
    }
    if (!accountNo.trim()) {
      handleError({ message: "Please enter account number" });
      return;
    }
    if (!accountName.trim()) {
      handleError({ message: "Please enter account name" });
      return;
    }
    if (!accountType.trim()) {
      handleError({ message: "Please enter account type" });
      return;
    }

    try {
      setIsLoading(true);
      const request: UpdateOfframpBankRequest = {
        bankId: bankData.bankId,
        bankName: bankName.trim(),
        accountNo: accountNo.trim(),
        accountName: accountName.trim(),
        accountType: accountType.trim(),
        isActive: isActive,
      };

      await invoiceServices.updateOfframpBank(request);

      handleSuccess({ message: "Bank updated successfully" });

      if (onBankUpdated) {
        onBankUpdated();
      }

      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "Failed to update bank";
      handleError({ message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivateDeactivate = async () => {
    if (!bankData?.bankId) {
      handleError({ message: "Bank ID is required" });
      return;
    }

    try {
      setIsLoading(true);
      if (isActive) {
        await invoiceServices.deactivateOfframpBank({
          bankId: bankData.bankId,
        });
        handleSuccess({ message: "Bank deactivated successfully" });
        setIsActive(false);
      } else {
        await invoiceServices.activateOfframpBank({ bankId: bankData.bankId });
        handleSuccess({ message: "Bank activated successfully" });
        setIsActive(true);
      }

      if (onBankUpdated) {
        onBankUpdated();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        `Failed to ${isActive ? "deactivate" : "activate"} bank`;
      handleError({ message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setBankName("");
    setAccountNo("");
    setAccountName("");
    setAccountType("");
    setIsActive(true);
  };

  // Populate form when bank data changes
  useEffect(() => {
    if (bankData && isOpen) {
      setBankName(bankData.bankName || "");
      setAccountNo(bankData.accountNo || "");
      setAccountName(bankData.accountName || "");
      setAccountType(bankData.accountType || "");
      setIsActive(Boolean(bankData.isActive));
    } else if (!isOpen) {
      resetForm();
    }
  }, [bankData, isOpen]);

  const isFormValid = Boolean(
    bankName.trim() &&
      accountNo.trim() &&
      accountName.trim() &&
      accountType.trim()
  );

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading={"Edit Bank"}
      onClose={() => {
        resetForm();
        onClose();
      }}
      onClickCross={() => {
        resetForm();
        onClose();
      }}
      showConfirm={false}
      isSlider={true}
      widthDesktop={"35%"}
    >
      <div className="flex flex-col px-[16px] py-[16px] mt-[60px]">
        <div className="flex flex-col gap-4 bg-bg-base rounded-lg p-4 border border-border-subtle">
          <GlobalTextInputField
            placeholder="Enter bank name"
            value={bankName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setBankName(e.target.value)
            }
            title="Bank Name"
          />

          <GlobalTextInputField
            placeholder="Enter account number"
            value={accountNo}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAccountNo(e.target.value)
            }
            title="Account Number"
          />

          <GlobalTextInputField
            placeholder="Enter account holder name"
            value={accountName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAccountName(e.target.value)
            }
            title="Account Name"
          />

          <GlobalTextInputField
            placeholder="Enter account type (e.g., Business Checking)"
            value={accountType}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAccountType(e.target.value)
            }
            title="Account Type"
          />

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-text-base">Status</label>
            <div className="flex items-center gap-2">
              <CheckBox
                checked={isActive}
                onChange={() => setIsActive(!isActive)}
              />
              <span className="text-sm text-text-base">Active</span>
            </div>
          </div>
          <CustomButton
            text="UPDATE BANK"
            onClick={handleSubmit}
            isActive={isFormValid && !isLoading}
            isButtonLoading={isLoading}
            className="flex-1 w-full"
          />
        </div>
      </div>
    </SlidingPanel>
  );
};

export default EditBankSlider;
